import { z } from 'zod'

/**
 * Why an inquiry was lost. Mirrors the `LostReason` enum in the Prisma schema —
 * the two must stay in sync.
 *
 * A fixed list, the same for every company, so venues' losses can be compared
 * and counted by cause; an optional note carries the rest. Spam, vendors,
 * duplicates and tests aren't lost: they never qualified, and the mistakes among
 * them are deleted. See `docs/funnel-stages.spec.md`.
 *
 * Like a cancellation reason, it lives on the **history row**, not on the event:
 * it belongs to the act of marking an inquiry lost, so one lost, reopened and
 * lost again keeps both reasons.
 */
export const LOST_REASONS = [
  'chose_another_venue',
  'over_budget',
  'date_not_available',
  'guest_count',
  'not_a_fit',
  'plans_changed',
  'stopped_responding',
  'other',
] as const

export const lostReasonSchema = z.enum(LOST_REASONS)

export type LostReason = z.infer<typeof lostReasonSchema>

/** How each reason reads on screen, so the form and any report agree. */
export const LOST_REASON_LABELS: Readonly<Record<LostReason, string>> = {
  chose_another_venue: 'Chose another venue',
  over_budget: 'Over budget',
  date_not_available: 'Date not available',
  guest_count: 'Guest count doesn’t fit',
  not_a_fit: 'Not the right fit',
  plans_changed: 'Plans changed',
  stopped_responding: 'Stopped responding',
  other: 'Other',
}

/**
 * Lost, counted by reason (docs/pipeline-view.spec.md): the reasons, then the
 * losses that recorded none, such as a couple lost in GoHighLevel. For counting
 * only; never stored.
 */
export const LOST_REASON_COUNT_KEYS = [...LOST_REASONS, 'not_recorded'] as const

export const lostReasonCountKeySchema = z.enum(LOST_REASON_COUNT_KEYS)

export type LostReasonCountKey = z.infer<typeof lostReasonCountKeySchema>

export const LOST_REASON_COUNT_LABELS: Readonly<Record<LostReasonCountKey, string>> = {
  ...LOST_REASON_LABELS,
  not_recorded: 'Not recorded',
}
